// Shared "was this element SEEN?" decision for impression tracking: one count per exposure.
//
// An impression counts each time its element comes into view (the decided convention, see
// doc/README--analytics.md → "Reading impression counts"). Deciding that from IntersectionObserver
// entries has two traps, and every impression tracker must handle both:
//
//   1. THE ZERO-AREA LIE. A rendered box with no area reports `isIntersecting: true` with
//      `intersectionRatio: 1`, so everything "counts" at once while the native apps hold their
//      webviews at 0x0. Both halves of the guard in `laid_out_intersection_observer.js` apply:
//      the observe is deferred until the target has area, and a zero-area entry is refused.
//   2. `isIntersecting` IS NOT A PORTABLE SIGNAL BELOW A THRESHOLD. The W3C spec defines it
//      geometrically ("intersect or are edge-adjacent"), independent of the threshold list, yet
//      Chrome and desktop WebKit both deliver a below-threshold entry with `isIntersecting: false`
//      (probed 2026-09-24, plans/2026-09-24--nav-tall-card-impressions.md). So "is this an
//      exposure?" and "has it left?" are judged from GEOMETRY here, never from `isIntersecting`.
//
// A third problem is a rule, not a trap: an element taller than its scroll root can never be
// fully visible, so a fully-visible rule never counts it. `fullyVisibleOrFillsRoot` also counts
// an element that fills most of the root, and DENSE_THRESHOLDS makes sure a callback arrives
// while it does.
//
// New impression trackers should use `ExposureObserver` rather than a bare IntersectionObserver.

import {observeWhenLaidOut, entryHasArea} from "./laid_out_intersection_observer";

// 0, 0.05, … 1.0. Guarantees a callback while an element up to ~20x the root's height fills the
// root, and the 0 step guarantees the "fully left" entry that re-arms the next exposure.
export const DENSE_THRESHOLDS = Array.from({length: 21}, (_, i) => i / 20);

// For the `anySliver` rule. `[0]` alone is not enough: an element first delivered edge-adjacent
// (intersecting at ratio 0, which re-arms rather than counts) produces no further entry as it
// scrolls in, because neither the threshold index nor `isIntersecting` changes. The small step
// guarantees an entry once a real sliver is inside the root.
export const ANY_SLIVER_THRESHOLDS = [0, 0.01];

// Sub-pixel rounding keeps a fully visible element's ratio a hair under 1.
export const FULLY_VISIBLE_RATIO = 0.99;
// A `rootMargin` enlarges the root, so an element exactly filling the visible root measures a
// little under 100% of it.
export const FILLS_ROOT_FRACTION = 0.9;

export const ExposureRules = {
    // Any real sliver of the element is inside the root.
    anySliver: (entry) => entry.intersectionRatio > 0,

    // Fully visible, or (for an element taller than the root) filling most of the root's height.
    fullyVisibleOrFillsRoot: (entry, { rootHeight } = {}) => {
        if (entry.intersectionRatio >= FULLY_VISIBLE_RATIO) return true;
        return rootHeight > 0 && entry.intersectionRect.height >= FILLS_ROOT_FRACTION * rootHeight;
    },
};

export const ExposureDecision = {
    count: 'count',   // this entry is (the start of) an exposure not yet counted
    rearm: 'rearm',   // the element has fully left; its next exposure counts again
    ignore: 'ignore', // nothing to do: not yet an exposure, already counted, or an untrustworthy entry
};

// What one entry means for an element's per-exposure count. Pure, so it can be tested directly.
//   - rule:  (entry, { rootHeight }) => boolean, e.g. `ExposureRules.fullyVisibleOrFillsRoot`
//   - state: { countedThisExposure, rootHeight }
export function exposureDecision(entry, rule, { countedThisExposure = false, rootHeight } = {}) {
    if (!entry || !entry.boundingClientRect || !entryHasArea(entry)) return ExposureDecision.ignore; // zero-area lie
    const rect = entry.intersectionRect;
    if (entry.intersectionRatio === 0 || !rect || rect.width === 0 || rect.height === 0) return ExposureDecision.rearm;
    if (countedThisExposure) return ExposureDecision.ignore;
    return rule(entry, { rootHeight }) ? ExposureDecision.count : ExposureDecision.ignore;
}

/**
 * Owns one element's IntersectionObserver and decides, once per exposure, when it was seen.
 *
 * `canCount` lets the caller withhold a count that is an exposure but may not be sent yet (the nav
 * withholds while its native drawer is shut). A withheld exposure is remembered and counted by
 * `flush()` once `canCount()` allows, provided the element hasn't left in the meantime.
 *
 * Call `destroy()` from the owning component's `onDestroy`.
 */
export class ExposureObserver {

    /**
     * @param {Element} target
     * @param {Object} options
     * @param {Element} [options.root] - the scroll root; the viewport when omitted.
     * @param {string} [options.rootMargin]
     * @param {number[]} [options.threshold] - defaults to DENSE_THRESHOLDS.
     * @param {Function} options.rule - see ExposureRules.
     * @param {Function} options.onExposure - sends the impression.
     * @param {Function} [options.canCount] - () => boolean; false withholds the count until `flush()`.
     * @param {string} [options.onNoResizeObserver] - passed to `observeWhenLaidOut` ('observe' | 'skip').
     */
    constructor(target, { root, rootMargin, threshold = DENSE_THRESHOLDS, rule, onExposure,
                          canCount = () => true, onNoResizeObserver = 'observe' }) {
        this.target = target;
        this.root = root;
        this.rule = rule;
        this.onExposure = onExposure;
        this.canCount = canCount;
        this.onNoResizeObserver = onNoResizeObserver;

        this.countedThisExposure = false;
        this.pendingWhileBlocked = false; // an exposure withheld by `canCount`, awaiting `flush()`
        this.laidOutObserver = null;      // the ResizeObserver deferring the first observe, if any

        this.observer = null;
        if (typeof IntersectionObserver === 'undefined') return;
        const options = { threshold };
        if (root) options.root = root;
        if (rootMargin) options.rootMargin = rootMargin;
        this.observer = new IntersectionObserver(this.handle.bind(this), options);
    }

    // Start observing, once the target has area. Starting is a fresh slate: nothing carried over.
    start() {
        if (!this.observer) return;
        this.cancelLaidOutObserver();
        this.resetExposure();
        this.laidOutObserver = observeWhenLaidOut(this.observer, this.target, {
            onNoResizeObserver: this.onNoResizeObserver,
        }) || null;
    }

    // Stop observing. Not being watched is a departure, so the next start counts afresh.
    stop() {
        if (!this.observer) return;
        this.cancelLaidOutObserver(); // or a deferred observe could start after we stopped
        this.observer.unobserve(this.target);
        this.resetExposure();
    }

    // Count a withheld exposure, if it may be counted now and the element hasn't left since.
    flush() {
        if (!this.pendingWhileBlocked || !this.canCount()) return;
        this.recordExposure();
    }

    destroy() {
        this.cancelLaidOutObserver();
        if (this.observer) this.observer.disconnect();
    }

    // IntersectionObserver callback. Judge only the NEWEST entry: it is the current truth, and an
    // older one queued in the same batch may describe a position the element has already left.
    handle(entries) {
        const entry = entries[entries.length - 1];
        const decision = exposureDecision(entry, this.rule, {
            countedThisExposure: this.countedThisExposure,
            rootHeight: this.getRootHeight(entry),
        });

        if (decision === ExposureDecision.rearm) {
            this.resetExposure();
        } else if (decision === ExposureDecision.count) {
            if (this.canCount()) {
                this.recordExposure();
            } else {
                this.pendingWhileBlocked = true;
            }
        }
    }

    recordExposure() {
        this.countedThisExposure = true;
        this.pendingWhileBlocked = false;
        this.onExposure();
    }

    resetExposure() {
        this.countedThisExposure = false;
        this.pendingWhileBlocked = false;
    }

    // `rootBounds` should always be present for an explicit root in the same document; fall back to
    // measuring the root (or the viewport) rather than silently disabling the fill rule.
    getRootHeight(entry) {
        if (entry && entry.rootBounds && entry.rootBounds.height > 0) return entry.rootBounds.height;
        if (this.root) return this.root.getBoundingClientRect().height;
        return window.innerHeight;
    }

    cancelLaidOutObserver() {
        if (!this.laidOutObserver) return;
        this.laidOutObserver.disconnect();
        this.laidOutObserver = null;
    }
}
